/*
 * Byte-identical in Cortex and Quantum.
 *
 * ONE PRIMARY KPI, ONE PRESENTATION.
 *
 * The strategy page shows the campaign's Primary KPI in many structured slots:
 * the hero pill, the hero summary card, the platform cards, the Platform Mix
 * KPI, the success-metrics table, the scenario cards, the Execution Blueprint's
 * Expected KPI, the measurement KPI list and the proposal's KPIs. Each reads its
 * KPI through formatMicrositePrimaryKpiDisplay, so no slot writes a metric or a
 * funnel stage differently from another.
 *
 * Presentation only. It changes letter case and nothing else (never a word, a
 * mark or their order), and it never writes back to the campaign:
 *   - a metric acronym reads in its canonical form whatever case it was saved
 *     in: "cpm" and "Cpm" → "CPM", "kpis" → "KPIs", "ecpm" → "eCPM";
 *   - a parenthetical that names only an objective / funnel stage reads as that
 *     stage's label: "(awareness)" → "(Awareness)",
 *     "(awareness / consideration)" → "(Awareness / Consideration)";
 *   - everything else keeps the case it was saved in. A KPI written as a
 *     sentence stays a sentence ("In-market unique reach, supported by CPM and
 *     frequency"), a parenthetical that is prose ("(measured in-flight)") is left
 *     as written, and nothing is title-cased.
 *
 * Why the hero pill reads campaign.primaryKpi: the backend saves the pill's
 * label as a title-cased copy of the KPI (routes/mediaCampaignRoutes.js,
 * titleCaseWords), which wrote "CPM" as "Cpm" and "In-market" as "In Market" on
 * Campaigns 53 and 55. The page takes the words from the campaign's own field;
 * the saved chip only says that the slot exists.
 */

// Metric acronyms in their canonical form, keyed by their lower-case spelling.
// A plural keeps the acronym and adds a lower-case "s" ("KPIs", "CPMs").
export const MICROSITE_KPI_ACRONYMS = Object.freeze({
  cpm: "CPM",
  cpc: "CPC",
  ctr: "CTR",
  cpa: "CPA",
  cpl: "CPL",
  cvr: "CVR",
  vcr: "VCR",
  vtr: "VTR",
  roas: "ROAS",
  kpi: "KPI",
  ctv: "CTV",
  cpv: "CPV",
  cpcv: "CPCV",
  cpe: "CPE",
  cpi: "CPI",
  roi: "ROI",
  ecpm: "eCPM",
  vcpm: "vCPM",
  ott: "OTT",
  olv: "OLV",
  dooh: "DOOH",
  grp: "GRP",
  trp: "TRP",
});

// The objective / funnel stages a KPI names in parentheses, as their labels
// write them. The page's objective labels name some stages as compound labels
// ("Reach / Awareness" in formatCampaignObjectiveLabel and the planner's goal
// options, "Awareness / Reach" in the proposal's stage), and a KPI must keep
// its own words, so a parenthetical keeps the stage word it names and takes
// that word's capitalisation from those labels.
export const MICROSITE_KPI_FUNNEL_LABELS = Object.freeze({
  awareness: "Awareness",
  reach: "Reach",
  consideration: "Consideration",
  engagement: "Engagement",
  traffic: "Traffic",
  conversion: "Conversion",
  conversions: "Conversions",
  retargeting: "Retargeting",
});

// An acronym is a whole token: letters and digits on neither side, so "ctrl",
// "roast" and "Ottawa" are words, not acronyms. (No lookbehind: older Safari
// cannot parse one.)
const ACRONYM_PATTERN = new RegExp(
  `(^|[^A-Za-z0-9])(${Object.keys(MICROSITE_KPI_ACRONYMS)
    .sort((left, right) => right.length - left.length)
    .join("|")})(s?)(?=$|[^A-Za-z0-9])`,
  "gi"
);

// A parenthetical of single words joined only by "/", "+" or "&".
const STAGE_PARENTHETICAL_PATTERN = /\(\s*[A-Za-z]+(?:\s*[/+&]\s*[A-Za-z]+)*\s*\)/g;

const isFunnelLabel = (word) =>
  Object.prototype.hasOwnProperty.call(MICROSITE_KPI_FUNNEL_LABELS, word.toLowerCase());

const formatStageParenthetical = (parenthetical) => {
  const words = parenthetical.match(/[A-Za-z]+/g);
  if (!words.every(isFunnelLabel)) return parenthetical;
  return parenthetical.replace(/[A-Za-z]+/g, (word) => MICROSITE_KPI_FUNNEL_LABELS[word.toLowerCase()]);
};

const kpiText = (value) => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (Array.isArray(value)) return value.map(kpiText).filter(Boolean).join("; ");
  return "";
};

export const formatMicrositePrimaryKpiDisplay = (value) =>
  kpiText(value)
    .replace(
      ACRONYM_PATTERN,
      (match, lead, acronym, plural) => `${lead}${MICROSITE_KPI_ACRONYMS[acronym.toLowerCase()]}${plural ? "s" : ""}`
    )
    .replace(STAGE_PARENTHETICAL_PATTERN, formatStageParenthetical);
